import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Skeleton
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import SchoolIcon from '@mui/icons-material/School';
import CategoryIcon from '@mui/icons-material/Category';
import ExtensionIcon from '@mui/icons-material/Extension';
import ScenarioIcon from '@mui/icons-material/Science';

export interface ContentItem {
  type: 'course' | 'category' | 'framework' | 'activity' | 'scenario' | 'folder' | 'file';
  name: string;
  id?: string;
  version?: string;
  size?: string;
  metadata?: Record<string, any>;
  children?: ContentItem[];
}

export interface ContentPreviewPanelProps {
  title?: string;
  items: ContentItem[];
  loading?: boolean;
  emptyMessage?: string;
  showMetadata?: boolean;
}

const getIconForType = (type: ContentItem['type']) => {
  switch (type) {
    case 'course':
      return <SchoolIcon />;
    case 'category':
      return <CategoryIcon />;
    case 'framework':
      return <ExtensionIcon />;
    case 'activity':
      return <ExtensionIcon color="primary" />;
    case 'scenario':
      return <ScenarioIcon />;
    case 'folder':
      return <FolderIcon />;
    case 'file':
      return <DescriptionIcon />;
    default:
      return <DescriptionIcon />;
  }
};

const ContentItemDisplay: React.FC<{ item: ContentItem; showMetadata?: boolean; depth?: number }> = ({
  item,
  showMetadata,
  depth = 0
}) => {
  return (
    <>
      <ListItem sx={{ pl: 2 + depth * 2 }}>
        <ListItemIcon>
          {getIconForType(item.type)}
        </ListItemIcon>
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">{item.name}</Typography>
              {item.version && (
                <Chip label={`v${item.version}`} size="small" variant="outlined" />
              )}
              {item.size && (
                <Typography variant="caption" color="text.secondary">
                  {item.size}
                </Typography>
              )}
            </Box>
          }
          secondary={
            showMetadata && item.metadata ? (
              <Box sx={{ mt: 0.5 }}>
                {Object.entries(item.metadata).map(([key, value]) => (
                  <Typography key={key} variant="caption" component="div" color="text.secondary">
                    {key}: {String(value)}
                  </Typography>
                ))}
              </Box>
            ) : item.id ? (
              <Typography variant="caption" color="text.secondary">
                ID: {item.id}
              </Typography>
            ) : null
          }
        />
      </ListItem>
      {item.children && item.children.length > 0 && (
        <>
          {item.children.map((child, index) => (
            <ContentItemDisplay
              key={`${child.name}-${index}`}
              item={child}
              showMetadata={showMetadata}
              depth={depth + 1}
            />
          ))}
        </>
      )}
    </>
  );
};

export const ContentPreviewPanel: React.FC<ContentPreviewPanelProps> = ({
  title = 'Content Preview',
  items,
  loading = false,
  emptyMessage = 'No content to preview',
  showMetadata = false
}) => {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        {title}
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {loading ? (
        <Box>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} height={60} />
          ))}
        </Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={3}>
          {emptyMessage}
        </Typography>
      ) : (
        <List dense>
          {items.map((item, index) => (
            <ContentItemDisplay
              key={`${item.name}-${index}`}
              item={item}
              showMetadata={showMetadata}
            />
          ))}
        </List>
      )}

      {!loading && items.length > 0 && (
        <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary">
            Total items: {items.length}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
